import { Button } from "@/components/ui/button";
import * as Sentry from "@sentry/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import {
  Calendar,
  Clock,
  Pencil,
  Phone,
  Plus,
  Search,
  Trash2,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import { ScheduleLeadCallDialog } from "../leads/ScheduleLeadCallDialog";
import { CancelLeadCallDialog } from "../leads/CancelLeadCallDialog";
import { CallErrorDialog } from "../leads/CallErrorDialog";
import { leadService } from "@/services/leadService";
import { ScheduledCall } from "@/models/ScheduledCall";
import { format } from "date-fns";
import { Input } from "../ui/input";
import { LeadSelector } from "./LeadSelector";
import { campaignService } from "@/services/campaignService";
import { featureTemplateService } from "@/services/featureTemplateService";
import outboundCallService from "@/services/outboundCallService";
import type { OutboundCallBackend } from "@/services/outboundCallService";
import { ConfirmCallDialog } from "./ConfirmCallDialog";

interface Lead {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  created_at: string;
}

interface LeadWithScheduledCall extends Lead {
  scheduledCall?: ScheduledCall;
}

interface CampaignLeadsListProps {
  campaignId: string;
  organizationId: string;
  onRefresh?: () => void;
}

export function CampaignLeadsList({
  campaignId,
  organizationId,
  onRefresh,
}: CampaignLeadsListProps) {
  const [leads, setLeads] = useState<LeadWithScheduledCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<"schedule" | "reschedule">("schedule");
  const [selectedLead, setSelectedLead] = useState<LeadWithScheduledCall | null>(null);
  
  // New state for direct calls
  const [confirmCallDialogOpen, setConfirmCallDialogOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [selectedLeadName, setSelectedLeadName] = useState("");
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState("");
  const [agentName, setAgentName] = useState("");
  const [callVariables, setCallVariables] = useState<Record<string, any>>({});
  const [assistantId, setAssistantId] = useState<string | null>(null);
  const [sourcePhoneNumberId, setSourcePhoneNumberId] = useState<string | null>(null);
  const [selectedFeatureTemplateName, setSelectedFeatureTemplateName] = useState("");
  const [selectedCallBackend, setSelectedCallBackend] =
    useState<OutboundCallBackend>("v1");
  
  // Error dialog state
  const [isCallErrorDialogOpen, setIsCallErrorDialogOpen] = useState(false);
  const [callErrorMessage, setCallErrorMessage] = useState("");

  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Function to handle direct call
  const handleDirectCall = async (
    leadId: string,
    phoneNumber?: string,
    leadName?: string,
  ) => {
    if (!phoneNumber) {
      toast({
        title: "Error",
        description: "No phone number available for this lead",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get campaign details to find the associated agent
      const { data: campaign, error: campaignError } = await campaignService.getCampaign(campaignId);
      if (campaignError) throw campaignError;
      if (!campaign) {
        throw new Error("Campaign not found");
      }

      // Get feature template to get agent details
      const featureTemplate = await featureTemplateService.getFeatureTemplate(
        campaign.feature_template_id,
      );
      if (!featureTemplate) {
        throw new Error("Feature template not found");
      }

      // Validate feature template has an assistant
      if (!featureTemplate.assistant?.id) {
        toast({
          title: "Error",
          description: "No assistant configured for this campaign. Please update feature template settings.",
          variant: "destructive",
        });
        return;
      }

      // Set the selected lead and agent information
      setSelectedLeadId(leadId);
      setSelectedLeadName(leadName || "Unknown Lead");
      setSelectedPhoneNumber(phoneNumber);
      setAgentName(featureTemplate.assistant.name);
      setAssistantId(featureTemplate.assistant.id);
      setSourcePhoneNumberId(campaign.phone_number_id);
      setSelectedFeatureTemplateName(featureTemplate.name);
      setSelectedCallBackend("v1");
      
      // Set call variables from campaign configuration (without campaign_id)
      setCallVariables(campaign.configuration_data || {});

      // Open the confirmation dialog
      setConfirmCallDialogOpen(true);
    } catch (error) {
      Sentry.logger.error('Error preparing call', { error: error, 
        log_source: 'campaign_leads_list',
        campaign_id: campaignId,
        lead_id: leadId,
        lead_name: leadName
      });
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to prepare call",
        variant: "destructive",
      });
    }
  };

  // Function to initiate the call
  const initiateDirectCall = async () => {
    if (!selectedLeadId || !assistantId) {
      toast({
        title: "Error",
        description: "Missing required call information. Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (selectedCallBackend === "v1" && !sourcePhoneNumberId) {
      toast({
        title: "Error",
        description: "V1 calls require an outbound phone number. Select V2 or update campaign settings.",
        variant: "destructive",
      });
      return;
    }

    try {
      toast({
        title: "Initiating call",
        description: "Connecting to lead...",
      });

      await outboundCallService.initiateCallToExistingLead(
        selectedLeadId,
        assistantId,
        sourcePhoneNumberId,
        organizationId,
        callVariables,
        campaignId, // Pass campaign_id as a separate parameter
        false,
        { backend: selectedCallBackend }
      );

      toast({
        title: "Call initiated",
        description: "Call has been successfully connected",
      });

      setConfirmCallDialogOpen(false);
      setSelectedCallBackend("v1");
    } catch (error) {
      Sentry.logger.error('Error initiating call', { error: error, 
        log_source: 'campaign_leads_list',
        campaign_id: campaignId,
        lead_id: selectedLeadId,
        assistant_id: assistantId,
        phone_number_id: sourcePhoneNumberId
      });
      
      // Extract detailed error message
      let errorMessage = "Failed to initiate call";
      if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }
      
      // Check if this is a prerequisite error that should show the detailed dialog
      if (errorMessage.includes("Missing required providers") || 
          errorMessage.includes("No active Plivo phone numbers found") ||
          errorMessage.includes("LLM provider") ||
          errorMessage.includes("STT provider") ||
          errorMessage.includes("TTS provider")) {
        setCallErrorMessage(errorMessage);
        setIsCallErrorDialogOpen(true);
      } else {
        // Show basic toast for other errors
        toast({
          title: "Call Failed",
          description: errorMessage,
          variant: "destructive",
          duration: 8000,
        });
      }
    }
  };

  // Function to open schedule dialog
  const openScheduleDialog = (
    lead: LeadWithScheduledCall,
    mode: "schedule" | "reschedule",
  ) => {
    setSelectedLead(lead);
    setScheduleMode(mode);
    setIsScheduleDialogOpen(true);
  };

  // Function to open cancel dialog
  const openCancelDialog = (lead: LeadWithScheduledCall) => {
    setSelectedLead(lead);
    setIsCancelDialogOpen(true);
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      // Fetch both campaign leads and scheduled calls in parallel
      const [campaignLeadsResult, scheduledCallsResult] = await Promise.all([
        // Get campaign leads with their lead data
        supabase
          .from("campaign_leads")
          .select(`
            id,
            lead_id,
            campaign_id,
            leads!inner(
              id,
              first_name,
              last_name,
              email,
              phone_number,
              created_at
            )
          `)
          .eq("campaign_id", campaignId),
        
        // Get all scheduled calls for this campaign directly
        supabase
          .from("scheduled_calls")
          .select("*")
          .eq("campaign_id", campaignId)
          .eq("status", "scheduled")
      ]);

      const { data: campaignLeadsData, error: campaignLeadsError } = campaignLeadsResult;
      const { data: scheduledCalls, error: scheduledCallsError } = scheduledCallsResult;
      
      if (campaignLeadsError) throw campaignLeadsError;
      if (scheduledCallsError) {
        Sentry.logger.error('Error fetching scheduled calls', { error: scheduledCallsError, 
          log_source: 'campaign_leads_list',
          campaign_id: campaignId
        });
      }

      if (campaignLeadsData && campaignLeadsData.length > 0) {
        // Create a map of scheduled calls by lead_id for efficient lookup
        const scheduledCallsMap = new Map(
          scheduledCalls?.map(call => [call.lead_id, call]) || []
        );

        // Map the leads with their scheduled calls
        const leadsWithScheduledCalls = campaignLeadsData
          .filter((item) => item.leads) // Filter out any null leads
          .map((item) => ({
            ...item.leads,
            scheduledCall: scheduledCallsMap.get(item.leads.id) || null,
          } as LeadWithScheduledCall));

        // Sort leads by created_at in descending order
        leadsWithScheduledCalls.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        setLeads(leadsWithScheduledCalls);
      } else {
        setLeads([]);
      }
    } catch (error) {
      Sentry.logger.error('Error fetching leads', { error: error, 
        log_source: 'campaign_leads_list',
        campaign_id: campaignId,
        organization_id: organizationId
      });
      toast({
        title: "Error",
        description: "Failed to fetch leads. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [campaignId, organizationId]);

  const handleLeadsAdded = () => {
    fetchLeads();
    if (onRefresh) {
      onRefresh();
    }
    toast({
      title: "Success",
      description: "Leads have been added to the campaign",
    });
  };

  const handleRemoveLead = async (leadId: string) => {
    try {
      const { error } = await campaignService.removeLeadFromCampaign(
        campaignId,
        leadId,
      );
      if (error) throw error;
      fetchLeads();
      if (onRefresh) {
        onRefresh();
      }
      toast({
        title: "Success",
        description: "Lead has been removed from the campaign",
      });
    } catch (error) {
      Sentry.logger.error('Error removing lead', { error: error, 
        log_source: 'campaign_leads_list',
        campaign_id: campaignId,
        lead_id: leadId
      });
      toast({
        title: "Error",
        description: "Failed to remove lead from campaign",
        variant: "destructive",
      });
    }
  };

  const filteredLeads = leads.filter((lead) => {
    const query = searchQuery.toLowerCase();
    return (
      lead.first_name.toLowerCase().includes(query) ||
      lead.last_name.toLowerCase().includes(query) ||
      lead.email?.toLowerCase().includes(query) ||
      lead.phone_number.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center relative max-w-md flex-1">
          <Search className="absolute left-3 text-muted-foreground" size={18} />
          <Input
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Leads
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Add Leads to Campaign</DialogTitle>
            </DialogHeader>
            <LeadSelector
              campaignId={campaignId}
              organizationId={organizationId}
              onComplete={handleLeadsAdded}
            />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="py-8 text-center">Loading...</div>
      ) : filteredLeads.length === 0 ? (
        <div className="py-8 text-center">
          <Users className="h-12 w-12 mx-auto text-ink-faint" />
          <p className="mt-2 text-muted-foreground">
            {searchQuery ? "No leads match your search" : "No leads in this campaign"}
          </p>
          <p className="text-sm text-ink-faint">
            {searchQuery
              ? "Try adjusting your search terms"
              : "Add leads to get started"}
          </p>
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone Number</TableHead>
                <TableHead>Scheduled Call</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeads.map((lead) => (
                <TableRow key={lead.id}>
                  <TableCell className="font-medium">
                    {lead.first_name} {lead.last_name}
                  </TableCell>
                  <TableCell>{lead.email || "-"}</TableCell>
                  <TableCell>{lead.phone_number}</TableCell>
                  <TableCell>
                    {lead.scheduledCall ? (
                      <span className="flex items-center text-foreground">
                        <Clock size={14} className="mr-1" />
                        {format(
                          new Date(lead.scheduledCall.scheduled_time),
                          "MMM d, yyyy h:mm a",
                        )}
                      </span>
                    ) : (
                      <span className="text-ink-faint">Not scheduled</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="primaryOutline"
                        size="icon"
                        onClick={() =>
                          handleDirectCall(
                            lead.id,
                            lead.phone_number,
                            `${lead.first_name} ${lead.last_name}`,
                          )
                        }
                        disabled={!lead.phone_number}
                        title={!lead.phone_number ? "No phone number available" : "Call Now"}
                      >
                        <Phone size={16} />
                      </Button>
                      {!lead.scheduledCall ? (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openScheduleDialog(lead, "schedule")}
                          title="Schedule Call"
                        >
                          <Calendar size={16} className="text-good" />
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => openScheduleDialog(lead, "reschedule")}
                            title="Reschedule Call"
                          >
                            <Calendar size={16} className="text-mid" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => openCancelDialog(lead)}
                            title="Cancel Scheduled Call"
                          >
                            <Clock size={16} className="text-danger" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleRemoveLead(lead.id)}
                        title="Remove from Campaign"
                      >
                        <Trash2 size={16} className="text-danger" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Schedule Call Dialog */}
      {selectedLead && (
        <ScheduleLeadCallDialog
          isOpen={isScheduleDialogOpen}
          onClose={() => setIsScheduleDialogOpen(false)}
          lead={selectedLead}
          onScheduled={fetchLeads}
          mode={scheduleMode}
          existingScheduledTime={selectedLead.scheduledCall?.scheduled_time}
          existingCallRequestId={selectedLead.scheduledCall?.id}
          campaignId={campaignId} // Pass campaignId to ScheduleLeadCallDialog
        />
      )}

      {/* Cancel Call Dialog */}
      {selectedLead && selectedLead.scheduledCall && (
        <CancelLeadCallDialog
          isOpen={isCancelDialogOpen}
          onClose={() => setIsCancelDialogOpen(false)}
          callRequestId={selectedLead.scheduledCall.id}
          leadName={`${selectedLead.first_name} ${selectedLead.last_name}`}
          onCancelled={fetchLeads}
        />
      )}

      {/* Confirm Call Dialog */}
      <ConfirmCallDialog
        open={confirmCallDialogOpen}
        onOpenChange={setConfirmCallDialogOpen}
        leadName={selectedLeadName}
        phoneNumber={selectedPhoneNumber}
        agentName={agentName}
        featureTemplateName={selectedFeatureTemplateName}
        variables={callVariables}
        backend={selectedCallBackend}
        onBackendChange={setSelectedCallBackend}
        onConfirm={initiateDirectCall}
      />

      {/* Call Error Dialog */}
      <CallErrorDialog
        isOpen={isCallErrorDialogOpen}
        onOpenChange={setIsCallErrorDialogOpen}
        errorMessage={callErrorMessage}
        onGoToProviders={() => navigate("/providers")}
      />
    </div>
  );
}
